/**
 * Every string `/content/:contentItemId/assets` renders.
 *
 * **The information lives at the control, not in a register at the foot of the
 * page.** A standing «what this surface does not do» section was dropped from
 * the design because every line of it was already said by the control it
 * qualified — the removal dialog names what goes with the thing, the refusal
 * explains the way out, and the upload hint announces the replacement.
 */
export const copy = {
  crumb: 'الأصول',

  head: {
    title: 'الأصول',
    ledeSeries:
      'هنا تُرفع ملفّات الفيديو نفسها. لكلّ عنوان مقطعٌ دعائيّ واحد، ثمّ حلقاتٌ موزّعةٌ على مواسم — ولكلّ موسم منطقة رفعه الخاصّة، فالحلقة تلتحق بموسمها من مكان إفلاتها. وترجمةُ كلّ ملفٍّ ترتفع بجانبه.',
    ledeFilm:
      'هنا تُرفع ملفّات الفيديو نفسها. لكلّ عنوان مقطعٌ دعائيّ واحد وملفٌّ واحد بلا مواسم، وترجمةُ كلّ ملفٍّ ترتفع بجانبه.',
  },

  shape: {
    heading: 'ما يحمله هذا العنوان الآن',
    type: 'النوع',
    seasons: 'المواسم',
    assets: 'الأصول',
    /** A film has zero seasons, so the row is absent rather than reading «٠». */
    seasonsNote: 'والموسم تقسيمٌ على الحلقات المرتّبة',
    titleSubtitleLead: 'ترجمة تخصّ العمل كلّه — تصلح لكلّ أصوله:',
    titleSubtitleAdd: 'أضف مساراً للعنوان',
  },

  trailer: {
    heading: '١. المقطع الدعائيّ',
    /** Free by kind, so a badge tells and no chip claims a control. */
    alwaysFree: 'مجّانيّ دائماً',
    lede: 'هو الإعلان، لا الفيلم ولا الحلقة. يُشغَّل لأيّ زائر مسجَّل الدخول اشترك أو لم يشترك، ولكلّ عنوان مقطعٌ واحد.',
    dropLabel: 'أفلت المقطع الدعائيّ هنا',
    dropHint: (formats: string) =>
      `ما يُفلَت هنا يصير المقطع الدعائيّ لهذا العنوان — لا حلقةً ولا فيلماً، ورفعُ مقطعٍ جديد يستبدل الحالي. الصيغ: ${formats}.`,
    empty: 'لا مقطع دعائيّ بعد.',
  },

  film: {
    heading: '٢. ملفّ الفيلم وترجماته',
    lede: 'للفيلم ملفٌّ واحد بلا مواسم. تُضبَط مجّانيّته من حقل «الوصول» في تحرير العنوان، وتُرفَع ترجماته داخل الملفّ نفسه.',
    dropLabel: 'أفلت ملفّ الفيلم هنا',
    dropHint: 'ما يُفلَت هنا يصير الفيلم نفسه، لا مقطعه الدعائيّ. ورفع ملفٍّ جديد يستبدل الحالي.',
    empty: 'لا ملفّ بعد. أفلت ملفّ الفيلم هنا.',
  },

  seasons: {
    heading: '٢. المواسم والحلقات',
    add: 'أضف موسماً',
    lede: 'أفلت حلقات كلّ موسم في منطقة رفعه، وستجد دقّات كلّ حلقة وترجماتها داخلها. رقاقة «مجّانيّة» تفتح الحلقة بلا اشتراك، ورقاقة الموسم تفتح حلقاته كلّها. وحذف الموسم يحذف حلقاته معه.',
    empty: 'لا موسم بعد. أضف واحداً ثمّ أفلت حلقاته فيه.',
    emptySeason: 'لا حلقة في هذا الموسم بعد.',
    freeSeason: 'الموسم كلّه مجّانيّ',
    /** A season holding no episodes opens nothing, and the chip says why. */
    freeSeasonDisabled: 'موسمٌ بلا حلقات لا يفتح شيئاً — أضف حلقةً أوّلاً.',
    edit: 'حرّر الموسم',
    remove: (name: string) => `احذف «${name}»`,
    moveUp: (name: string) => `انقل «${name}» إلى الأعلى`,
    moveDown: (name: string) => `انقل «${name}» إلى الأسفل`,
    dropLabel: (name: string) => `أفلت حلقات «${name}» هنا`,
    dropHint: (name: string) =>
      `ما يُفلَت هنا يصير حلقةً في «${name}» ويأخذ آخر رتبةٍ فيه — لا مقطعاً دعائياً ولا حلقةً في موسمٍ آخر.`,
    subtitleLead: (name: string) => `ترجمة تخصّ «${name}» كلّه:`,
    subtitleAdd: 'أضف مساراً للموسم',
    newTitle: 'موسم جديد',
    added: 'أُضيف الموسم. سمِّه الآن.',
  },

  /**
   * The one image a season or an episode carries.
   *
   * Worded around what the operator is looking at rather than around the
   * service's vocabulary: «الصورة» and not «purpose thumbnail», because the
   * subject has exactly one and naming the purpose would be naming an
   * implementation the operator never chooses between.
   */
  artwork: {
    heading: 'الصورة',
    add: 'أضف صورة',
    replace: 'استبدل الصورة',
    uploading: 'جارٍ الرفع…',
    /**
     * Said only when the wait ran out — the ordinary path now SHOWS the image.
     *
     * The upload holds its busy state until the derived row appears, so a
     * successful upload ends with the picture in the box and nothing to read.
     * This line is what remains for the case the crop did not land inside the
     * window: `assets` marks a job `FAILED` without telling this console, and a
     * silent empty box is what the operator met before.
     */
    notDerived: 'رُفعت الصورة، لكنّها لم تظهر بعد. أعد المحاولة أو حدّث الصفحة لتتحقّق.',
    failed: 'تعذّر رفع الصورة. تحقّق من المقاس والصيغة ثمّ أعد المحاولة.',
    hint: (name: string, ratio: string, minimum: string, formats: string) =>
      `تظهر هذه الصورة مع «${name}» في قائمة المشاهد. النسبة ${ratio} وأصغر مقاس مقبول ${minimum} بكسل — وما دونه يُرفض بعد اكتمال الرفع لا قبله. الصيغ: ${formats}.`,
  },

  asset: {
    edit: 'حرّر',
    free: 'مجّانيّة بلا اشتراك',
    moveUp: (name: string) => `انقل «${name}» إلى الأعلى`,
    moveDown: (name: string) => `انقل «${name}» إلى الأسفل`,
    remove: (name: string) => `احذف «${name}»`,
    renditions: 'الدقّات المتاحة',
    renditionsCaption: (name: string) => `دقّات ${name}`,
    renditionColumns: { label: 'الدقّة', size: 'الحجم', state: 'الحالة' },
    documents: 'الترجمات',
    documentsEmpty: 'لا مسار ترجمة بعد.',
    documentAdd: 'أضف مسار ترجمة',
    documentHint: (name: string, formats: string) =>
      `يلتحق المسار بـ«${name}» وحده، ويصدر تحت استحقاق العنوان نفسه. الصيغ: ${formats}. التوقيت يأتي من الملفّ كما هو — تأكّد أنّه مضبوطٌ على هذه النسخة قبل الرفع.`,
    documentRemove: (name: string) => `احذف «${name}»`,
    uploading: 'جارٍ الرفع…',

    /**
     * The encode attempt behind the ladder.
     *
     * The block is drawn only when a job exists, and it says which rungs were
     * asked for and what became of each — because a rung that reads «تُخُطِّيَت»
     * in the ladder table is otherwise indistinguishable from one the encoder
     * failed on.
     */
    encode: {
      heading: 'حالة الترميز',
      caption: (name: string) => `مهمّة ترميز ${name}`,
      columns: { rung: 'الدقّة المطلوبة', outcome: 'ما آل إليه' },
      attempts: (count: string) => `عدد المحاولات: ${count}`,
      /** The service records no detail on most failures; the code is then the whole reason. */
      noReason: 'لم تُسجَّل الخدمة سبباً أدقّ من الرمز.',
      reason: (code: string) => `رمز الخطأ: ${code}`,
      retry: 'أعد محاولة الترميز',
      retrying: 'جارٍ إرسال الطلب…',
      retried: 'أُرسل طلب إعادة الترميز. حدّث الصفحة بعد قليل لترى النتيجة.',
      /** Only a failed job is retried: a running one is already doing the work. */
      running: 'المهمّة قيد التنفيذ.',
      none: 'لا مهمّة ترميز لهذا الملفّ بعد.',
    },
  },

  dialog: {
    assetTitle: 'حرّر الأصل',
    groupTitle: 'حرّر الموسم',
    nameLabel: 'العنوان',
    descriptionLabel: 'الوصف',
    save: 'احفظ',
    cancel: 'ألغِ',
  },

  remove: {
    asset: {
      heading: (name: string) => `احذف «${name}»؟`,
      body: (name: string) =>
        `تُحذف «${name}» ومعها دقّاتها ومسارات ترجمتها، وتُزاح رتب ما بعدها. لا رجعة في هذا.`,
      commit: 'احذف الملفّ',
    },
    trailer: {
      heading: 'احذف المقطع الدعائيّ؟',
      body: 'يُحذف المقطع الدعائيّ ومعه دقّاته ومسارات ترجمته. يبقى العنوان بلا مقطعٍ حتّى يُرفَع غيره. لا رجعة في هذا.',
      commit: 'احذف المقطع',
    },
    group: {
      heading: (name: string) => `احذف «${name}»؟`,
      /** The count is what makes the sentence worth reading before the press. */
      body: (name: string, episodes: string) =>
        `يُحذف «${name}» ومعه ${episodes} كلّها ودقّاتها ومسارات ترجمتها. الموسم قسمةٌ على الحلقات لا مجلّدٌ بجانبها، فحذفه يحذفها. لا رجعة في هذا.`,
      commit: 'احذف الموسم وحلقاته',
    },
    document: {
      heading: 'احذف مسار الترجمة؟',
      body: (name: string, owner: string) =>
        `يُحذف «${name}» من «${owner}» ويُحذف ملفّه. لا أثر على الفيديو نفسه.`,
      commit: 'احذف المسار',
    },
    /** The one refusal, drawn as a refusal rather than as a missing button. */
    refusal: {
      heading: 'لا يمكن حذف الملفّ الوحيد',
      body: 'هذا الملفّ الوحيد تحت عنوانٍ منشور، وحذفه يترك صفحةً منشورة بلا شيء يُشغَّل. ألغِ النشر أوّلاً من قائمة المحتوى، ثمّ احذف.',
      commit: 'حسناً',
    },
  },

  /** Said once at the end rather than repeated on every rung. */
  streamOnly: 'كلّ الدقّات تُشاهَد أونلاين ولا تُنزَّل — مجّانيّاً كان العنوان أم بالاشتراك.',

  states: {
    loading: 'جارٍ تحميل الأصول',
  },

  /** What a zone says about its own upload, under that zone and nowhere else. */
  upload: {
    unsupportedFormat: (accepted: string) => `صيغة الملف غير مدعومة. الصيغ المقبولة: ${accepted}.`,
    tooLarge: (maxMegabytes: string) => `حجم الملف يتجاوز الحد المسموح (${maxMegabytes} م.ب).`,
    policyUnavailable: 'تعذّر قراءة إعدادات الرفع. حدّث الصفحة ثمّ أعد المحاولة.',
  },

  toast: {
    uploaded: 'رُفع الملفّ. الترميز يعمل الآن.',
    markedFree: 'صار الملفّ مجّانيّاً — يُشاهَد بلا اشتراك.',
    unmarkedFree: 'عاد الملفّ خلف الاشتراك.',
    removed: 'حُذف.',
    documentUploaded: 'أُضيف مسار الترجمة.',
    reordered: 'تغيّر الترتيب.',
    /** A removal whose outcome is unknown is not retried from the client. */
    unknownOutcome: 'لا نعرف نتيجة الحذف. أعد تحميل الصفحة للاطّلاع عليها.',
  },
} as const;
